import React, { useState } from "react";
import { useEffect } from "react";
import api from "../utils/api"; // adjust if your api helper path differs
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Switch,
  Button,
  Stack,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MapIcon from "@mui/icons-material/Map";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Reuse the same SettingRow pattern used elsewhere in Settings
function SettingRow({ label, description, checked, onChange, disabled }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1.5,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box>
        <Typography fontWeight={600}>{label}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    </Box>
  );
}

function StatusChip({ enabled, configured = true }) {
  const label = enabled ? "Enabled" : configured ? "Disabled" : "Not configured";
  const color = enabled ? "success" : "default";

  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={enabled ? "filled" : "outlined"}
      sx={{
        height: 20,
        fontSize: "0.66rem",
        fontWeight: 700,
      }}
    />
  );
}

/** CompanyProfile pref: null/undefined = automatic (on when env reports configured). */
function integrationToggleFromPref(pref, envConfigured) {
  if (pref === false) return false;
  if (pref === true) return true;
  return !!envConfigured;
}

export default function EmailSettingsPage() {
  const theme = useTheme();
  const { isReadOnly, role } = useAuth();
  const integrationReadOnly = isReadOnly || role === "Sales Director";

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [followupIntelligenceAutomationEnabled, setFollowupIntelligenceAutomationEnabled] =
    useState(false);

  const [smtpSettings, setSmtpSettings] = useState({
    fromName: "",
    fromEmail: "",
    replyTo: "",
    host: "",
    port: "587",
    username: "",
    password: "",
    useTls: true,
  });

  const [defaults, setDefaults] = useState({
    leadAssigned: true,
    taskAssigned: true,
    followupCreated: true,
    followupDue: true,
    messageReceived: true,
    systemNotifications: false,
  });

  // Twilio / SMS state (API wiring later)
  const [smsEntitled] = useState(true); // TODO: from subscription/entitlements API
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsSettings, setSmsSettings] = useState({
    twilioNumber: "",
    accountSid: "",
    authToken: "",
  });

  // Web Push state (config/status - already implemented via VAPID env)
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSettings, setPushSettings] = useState({
    vapidPublicKey: "",
    vapidPrivateKey: "",
  });

  // Mapbox state (API wiring later)
  const [mapboxEnabled, setMapboxEnabled] = useState(false);
  const [mapboxSettings, setMapboxSettings] = useState({
    accessToken: "",
  });

  // Webhooks / Zapier state (API wiring later)
  const [webhooksEnabled, setWebhooksEnabled] = useState(false);
  const [webhooksSettings, setWebhooksSettings] = useState({
    webhookUrl: "",
    apiKey: "",
  });
  const [fillQuickSettings, setFillQuickSettings] = useState({
    userId: "",
    apiKey: "",
    salesRepId: "",
    leadSourceId: "1",
  });

  /** null = not loaded yet; drives StatusChip "configured" from server env (no secrets). */
  const [serverIntegrationStatus, setServerIntegrationStatus] = useState(null);

  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const res = await api.get("/settings/email/");

        setEmailEnabled(res.data.enabled);

        setSmtpSettings({
          fromName: res.data.from_name || "",
          fromEmail: res.data.from_email || "",
          replyTo: res.data.reply_to || "",
          host: res.data.smtp_host || "",
          port: String(res.data.smtp_port || 587),
          username: res.data.smtp_username || "",
          password: "", // never preload passwords
          useTls: res.data.use_tls ?? true,
        });
      } catch (err) {
        console.error("Failed to load email settings", err);
        if (!(role === "Sales Director" && err?.response?.status === 403)) {
          toast.error("Failed to load email settings");
        }
      }
    };

    const loadProfileAndIntegrations = async () => {
      const [profOutcome, integOutcome] = await Promise.allSettled([
        api.get("/company-profile/"),
        api.get("/settings/integrations-status/"),
      ]);

      let profile = {};
      if (profOutcome.status === "fulfilled") {
        profile = profOutcome.value.data || {};
        setFollowupIntelligenceAutomationEnabled(
          !!profile.followup_intelligence_automation_enabled
        );
        setFillQuickSettings((prev) => ({
          ...prev,
          userId: profile.fillquick_user_id || "",
          apiKey: profile.fillquick_api_key || "",
          salesRepId:
            profile.fillquick_salesrep_id !== null &&
            profile.fillquick_salesrep_id !== undefined
              ? String(profile.fillquick_salesrep_id)
              : "",
          leadSourceId: String(profile.fillquick_lead_source_id || 1),
        }));
      } else {
        console.error("Failed to load company profile", profOutcome.reason);
      }

      let ig = {};
      if (integOutcome.status === "fulfilled") {
        ig = integOutcome.value.data || {};
        setServerIntegrationStatus({
          twilio_configured: !!ig.twilio_configured,
          mapbox_configured: !!ig.mapbox_configured,
          web_push_configured: !!ig.web_push_configured,
        });
        if (ig.twilio_phone_number) {
          setSmsSettings((prev) => ({
            ...prev,
            twilioNumber: ig.twilio_phone_number,
          }));
        }
        if (ig.vapid_public_key) {
          setPushSettings((prev) => ({
            ...prev,
            vapidPublicKey: ig.vapid_public_key,
          }));
        }
      } else {
        console.error("Failed to load integrations status", integOutcome.reason);
        setServerIntegrationStatus(null);
      }

      const twilioOk =
        integOutcome.status === "fulfilled" && !!ig.twilio_configured;
      const pushOk =
        integOutcome.status === "fulfilled" && !!ig.web_push_configured;
      const mapboxOk =
        integOutcome.status === "fulfilled" && !!ig.mapbox_configured;

      if (
        profOutcome.status === "fulfilled" ||
        integOutcome.status === "fulfilled"
      ) {
        setSmsEnabled(
          integrationToggleFromPref(
            profile.sms_integration_enabled,
            twilioOk
          )
        );
        setPushEnabled(
          integrationToggleFromPref(
            profile.web_push_integration_enabled,
            pushOk
          )
        );
        setMapboxEnabled(
          integrationToggleFromPref(
            profile.mapbox_integration_enabled,
            mapboxOk
          )
        );
      }
    };

    loadEmailSettings();
    loadProfileAndIntegrations();
  }, []);

  const handleSmtpChange = (field, value) => {
    setSmtpSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
  if (integrationReadOnly) return;
  try {
    await api.post("/settings/email/", {
      enabled: emailEnabled,
      from_name: smtpSettings.fromName,
      from_email: smtpSettings.fromEmail,
      reply_to: smtpSettings.replyTo,
      smtp_host: smtpSettings.host,
      smtp_port: Number(smtpSettings.port),
      smtp_username: smtpSettings.username,
      smtp_password: smtpSettings.password,
      use_tls: smtpSettings.useTls,
    });

    toast.success("Email settings saved");
    setSmtpSettings((prev) => ({ ...prev, password: "" }));
  } catch (err) {
    console.error("Failed to save email settings", err);
    toast.error("Failed to save email settings");
  }
};


  const handleSendTest = () => {
    // TODO: wire test email endpoint
    console.log("Send test email");
  };

  const handleFollowupIntelligenceToggle = async (checked) => {
    if (integrationReadOnly) return;
    try {
      await api.patch("/company-profile/", {
        followup_intelligence_automation_enabled: checked,
      });
      setFollowupIntelligenceAutomationEnabled(checked);
      toast.success(
        checked
          ? "Follow-up intelligence automation enabled"
          : "Follow-up intelligence automation disabled"
      );
    } catch (err) {
      console.error("Failed to save follow-up automation setting", err);
      toast.error("Failed to update follow-up automation setting");
    }
  };

  const patchIntegrationPreference = async (field, checked, label) => {
    if (integrationReadOnly) return;
    let revert = () => {};
    if (field === "sms_integration_enabled") {
      setSmsEnabled((prev) => {
        revert = () => setSmsEnabled(prev);
        return checked;
      });
    } else if (field === "web_push_integration_enabled") {
      setPushEnabled((prev) => {
        revert = () => setPushEnabled(prev);
        return checked;
      });
    } else if (field === "mapbox_integration_enabled") {
      setMapboxEnabled((prev) => {
        revert = () => setMapboxEnabled(prev);
        return checked;
      });
    }
    try {
      await api.patch("/company-profile/", { [field]: checked });
      toast.success(checked ? `${label} enabled` : `${label} disabled`);
    } catch (err) {
      revert();
      console.error("Failed to save integration preference", err);
      toast.error("Failed to update integration setting");
    }
  };

  const handleSmsSettingsChange = (field, value) => {
    setSmsSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendTestSms = () => {
    // TODO: wire test SMS endpoint
    console.log("Send test SMS");
  };

  const handleSaveSms = () => {
    // TODO: wire SMS settings API
    toast.info("SMS settings save - API not yet implemented");
  };

  const handlePushSettingsChange = (field, value) => {
    setPushSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSavePush = () =>
    toast.info("Web Push settings save - API not yet implemented");

  const handleMapboxSettingsChange = (field, value) => {
    setMapboxSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveMapbox = () =>
    toast.info("Mapbox settings save - API not yet implemented");

  const handleWebhooksSettingsChange = (field, value) => {
    setWebhooksSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveWebhooks = () =>
    toast.info("Webhooks settings save - API not yet implemented");
  const handleFillQuickSettingsChange = (field, value) => {
    setFillQuickSettings((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveFillQuick = async () => {
    if (integrationReadOnly) return;
    try {
      await api.patch("/company-profile/", {
        fillquick_user_id: fillQuickSettings.userId || "",
        fillquick_api_key: fillQuickSettings.apiKey || "",
        fillquick_salesrep_id: fillQuickSettings.salesRepId
          ? Number(fillQuickSettings.salesRepId)
          : null,
        fillquick_lead_source_id: Number(fillQuickSettings.leadSourceId || 1),
      });
      toast.success("FillQuick settings saved");
    } catch (err) {
      console.error("Failed to save FillQuick settings", err);
      toast.error("Failed to save FillQuick settings");
    }
  };

  const disabledSection = integrationReadOnly || !emailEnabled;
  const disabledSmsSection = integrationReadOnly || !smsEnabled;
  const disabledPushSection = integrationReadOnly || !pushEnabled;
  const disabledMapboxSection = integrationReadOnly || !mapboxEnabled;
  const disabledWebhooksSection = integrationReadOnly || !webhooksEnabled;
  const smsConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.twilio_configured
      : Boolean(
          smsSettings.twilioNumber?.trim() &&
            smsSettings.accountSid?.trim() &&
            smsSettings.authToken?.trim()
        );
  const pushConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.web_push_configured
      : Boolean(
          pushSettings.vapidPublicKey?.trim() &&
            pushSettings.vapidPrivateKey?.trim()
        );
  const mapboxConfigured =
    serverIntegrationStatus !== null
      ? serverIntegrationStatus.mapbox_configured
      : Boolean(mapboxSettings.accessToken?.trim());
  const webhooksConfigured = Boolean(webhooksSettings.webhookUrl?.trim());
  const fillQuickConfigured = Boolean(
    fillQuickSettings.userId?.trim() && fillQuickSettings.apiKey?.trim()
  );
  const followupConfigured = emailEnabled || smsEnabled;

  return (
    <Box sx={{ p: 4, px: { xs: 1, sm: 4 } }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {/* ---------------- Header Card ---------------- */}
      <Card sx={{ p: 3, mb: 5, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(25,118,210,0.08)",
              }}
            >
              <EmailIcon color="primary" />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Email Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System-wide email configuration (Admin only)
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleSendTest}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Send Test Email
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Stack>
        </Stack>
        {role === "Sales Director" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            View-only access: only Admin can modify integrations settings.
          </Typography>
        )}
      </Card>

      {/* ---------------- Main Grid ---------------- */}
      <Box
        data-tour="integrations-email"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
        gap={4}
        sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
      >
        {/* ---------------- Card 1: Global Toggle ---------------- */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Email Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable or disable all outgoing email from the CRM.
            </Typography>

            <SettingRow
              label="Enable Email Notifications"
              checked={emailEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                setEmailEnabled(checked);
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        {/* ---------------- Card 2: SMTP Provider ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Email Provider (SMTP)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure how emails are sent from the system.
            </Typography>

            <Stack spacing={2}>
              <TextField
                size="small"
                label="From Name"
                value={smtpSettings.fromName}
                onChange={(e) => handleSmtpChange("fromName", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="From Email"
                value={smtpSettings.fromEmail}
                onChange={(e) => handleSmtpChange("fromEmail", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="Reply-To Email"
                value={smtpSettings.replyTo}
                onChange={(e) => handleSmtpChange("replyTo", e.target.value)}
                disabled={disabledSection}
              />

              <Box sx={{ height: 8 }} />

              <TextField
                size="small"
                label="SMTP Host"
                value={smtpSettings.host}
                onChange={(e) => handleSmtpChange("host", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="SMTP Port"
                value={smtpSettings.port}
                onChange={(e) => handleSmtpChange("port", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                label="SMTP Username"
                value={smtpSettings.username}
                onChange={(e) => handleSmtpChange("username", e.target.value)}
                disabled={disabledSection}
              />
              <TextField
                size="small"
                type="password"
                label="SMTP Password"
                value={smtpSettings.password}
                onChange={(e) => handleSmtpChange("password", e.target.value)}
                disabled={disabledSection}
              />

              <SettingRow
                label="Use TLS"
                checked={smtpSettings.useTls}
                onChange={(val) => handleSmtpChange("useTls", val)}
                disabled={disabledSection}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ---------------- Card 3: Default Preferences ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Default Email Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Applied to new users. Individuals can override later.
            </Typography>

            <Stack spacing={1}>
              <SettingRow
                label="Lead assigned"
                description="When a lead is assigned to a user"
                checked={defaults.leadAssigned}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, leadAssigned: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Task assigned"
                description="When a task is assigned"
                checked={defaults.taskAssigned}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, taskAssigned: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Follow-up created"
                description="When a follow-up is created"
                checked={defaults.followupCreated}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, followupCreated: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="Follow-up due"
                description="Reminder on the day a follow-up is due"
                checked={defaults.followupDue}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, followupDue: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="New internal message"
                description="When a message is received"
                checked={defaults.messageReceived}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, messageReceived: val }))
                }
                disabled={disabledSection}
              />
              <SettingRow
                label="System notifications"
                description="Low priority system messages"
                checked={defaults.systemNotifications}
                onChange={(val) =>
                  setDefaults((p) => ({ ...p, systemNotifications: val }))
                }
                disabled={disabledSection}
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* ---------------- Follow-Up Intelligence (automation) ---------------- */}
      <Accordion defaultExpanded sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <AutoAwesomeIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Follow-Up Intelligence
                  </Typography>
                  <StatusChip
                    enabled={followupIntelligenceAutomationEnabled}
                    configured={followupConfigured}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Automated SMS/email to leads with overdue follow-ups (scheduled job). Turn off until you are ready to go live.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0 }}>
          <SettingRow
            label="Enable automated overdue follow-up messages"
            description="Requires SMTP and/or Twilio configured. When disabled, no automation sends run."
            checked={followupIntelligenceAutomationEnabled}
            onChange={handleFollowupIntelligenceToggle}
            disabled={integrationReadOnly}
          />
        </AccordionDetails>
      </Accordion>

      <Box data-tour="integrations-services">
      {/* ---------------- Twilio / SMS Section ---------------- */}
      <Accordion sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <SmsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    SMS (Twilio)
                  </Typography>
                  <StatusChip enabled={smsEnabled} configured={smsConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Allows Two-way SMS conversations with leads within the CRM. Subscribe or add your existing Twilio Credentials
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        {/* ---------------- Card 1: SMS Global Toggle ---------------- */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              SMS Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable or disable SMS conversations with leads.
            </Typography>
            <SettingRow
              label="Enable SMS Conversations"
              checked={smsEnabled}
              onChange={(checked) => {
                if (integrationReadOnly || !smsEntitled) return;
                patchIntegrationPreference(
                  "sms_integration_enabled",
                  checked,
                  "SMS conversations"
                );
              }}
              disabled={integrationReadOnly || !smsEntitled}
            />
          </CardContent>
        </Card>

        {/* ---------------- Card 2: Twilio Provider ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSmsSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Twilio Provider
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure Twilio phone number for SMS.
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Twilio Phone Number"
                placeholder="+1234567890"
                value={smsSettings.twilioNumber}
                onChange={(e) =>
                  handleSmsSettingsChange("twilioNumber", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>

        {/* ---------------- Card 3: Twilio Credentials ---------------- */}
        <Card sx={{ borderRadius: 3, opacity: disabledSmsSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Twilio Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Account SID and Auth Token from your Twilio console.
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Account SID"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={smsSettings.accountSid}
                onChange={(e) =>
                  handleSmsSettingsChange("accountSid", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
              <TextField
                size="small"
                type="password"
                label="Auth Token"
                placeholder="Your secret auth token"
                value={smsSettings.authToken}
                onChange={(e) =>
                  handleSmsSettingsChange("authToken", e.target.value)
                }
                disabled={disabledSmsSection}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleSendTestSms}
                sx={{ textTransform: "none" }}
                disabled={!smsEnabled || integrationReadOnly}
              >
                Send Test SMS
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSms}
                sx={{ textTransform: "none" }}
                disabled={integrationReadOnly}
              >
                Save SMS Settings
              </Button>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- FillQuick CRM Integration ---------------- */}
      <Accordion sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <IntegrationInstructionsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    FillQuick CRM
                  </Typography>
                  <StatusChip enabled={fillQuickConfigured} configured={fillQuickConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Admin setup for FillQuick lead creation (push Closed Won leads from Territory CRM).
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={700} mb={1}>
                FillQuick API Credentials (Admin)
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter the FillQuick User ID and API key provided by FillQuick admin setup.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="FillQuick User ID"
                  value={fillQuickSettings.userId}
                  onChange={(e) => handleFillQuickSettingsChange("userId", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  type="password"
                  label="FillQuick API Key"
                  value={fillQuickSettings.apiKey}
                  onChange={(e) => handleFillQuickSettingsChange("apiKey", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Default FillQuick Sales Rep ID (optional)"
                  value={fillQuickSettings.salesRepId}
                  onChange={(e) => handleFillQuickSettingsChange("salesRepId", e.target.value)}
                  disabled={integrationReadOnly}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Lead Source ID (self-generated)"
                  value={fillQuickSettings.leadSourceId}
                  disabled
                  fullWidth
                  helperText="Default FillQuick lead source for self-generated leads is 1."
                />
              </Stack>
            </CardContent>
          </Card>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveFillQuick}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save FillQuick Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Web Push Section ---------------- */}
      <Accordion sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <NotificationsActiveIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Web Push
                  </Typography>
                  <StatusChip enabled={pushEnabled} configured={pushConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enables Team Chat @mentions and browser notifications from the CRM. Included feature. Request credentials
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Web Push Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable browser push for Team Chat @mentions.
            </Typography>
            <SettingRow
              label="Enable Web Push"
              checked={pushEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                patchIntegrationPreference(
                  "web_push_integration_enabled",
                  checked,
                  "Web push"
                );
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledPushSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              VAPID Public Key
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Used by frontend to subscribe. Set REACT_APP_VAPID_PUBLIC_KEY in env.
            </Typography>
            <TextField
              size="small"
              label="VAPID Public Key"
              placeholder="BKxxxxxxxx..."
              value={pushSettings.vapidPublicKey}
              onChange={(e) =>
                handlePushSettingsChange("vapidPublicKey", e.target.value)
              }
              disabled={disabledPushSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledPushSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              VAPID Private Key
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Backend only. Set VAPID_PRIVATE_KEY in env. Never expose.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="VAPID Private Key"
              placeholder="••••••••"
              value={pushSettings.vapidPrivateKey}
              onChange={(e) =>
                handlePushSettingsChange("vapidPrivateKey", e.target.value)
              }
              disabled={disabledPushSection}
              fullWidth
            />
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSavePush}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Mapbox Geocoding Section ---------------- */}
      <Accordion sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <MapIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Mapbox Geocoding
                  </Typography>
                  <StatusChip enabled={mapboxEnabled} configured={mapboxConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enables Address geocoding for leads and territories. For high volume geocoding visit: https://www.mapbox.com/, for your API Token
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Geocoding
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable Mapbox for address → lat/lon. Falls back to geocode.maps.co and Nominatim if disabled.
            </Typography>
            <SettingRow
              label="Enable Mapbox Geocoding"
              checked={mapboxEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                patchIntegrationPreference(
                  "mapbox_integration_enabled",
                  checked,
                  "Mapbox geocoding"
                );
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledMapboxSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Mapbox Access Token
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              From Mapbox account. Set GEOCODE_MAPBOX_ACCESS_TOKEN or MAPBOX_ACCESS_TOKEN in env.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="Access Token"
              placeholder="pk.xxxxxxxx..."
              value={mapboxSettings.accessToken}
              onChange={(e) =>
                handleMapboxSettingsChange("accessToken", e.target.value)
              }
              disabled={disabledMapboxSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledMapboxSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Info
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mapbox is used for lead import, territory mapping, and address geocoding. When disabled, the system falls back to free services (geocode.maps.co, Nominatim).
            </Typography>
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveMapbox}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------------- Webhooks / Zapier Section ---------------- */}
      <Accordion sx={{ mb: 4, borderRadius: 3, mt: 6, overflow: "hidden" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <IntegrationInstructionsIcon color="primary" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    Webhooks / Zapier
                  </Typography>
                  <StatusChip enabled={webhooksEnabled} configured={webhooksConfigured} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Send events to external tools (Zapier, Make, etc.)
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={4}
            sx={{ "& .MuiCardContent-root": { px: 3, py: 2.5 } }}
          >
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Webhooks
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable outbound webhooks for lead created, conversation updated, etc.
            </Typography>
            <SettingRow
              label="Enable Webhooks"
              checked={webhooksEnabled}
              onChange={(checked) => {
                if (integrationReadOnly) return;
                setWebhooksEnabled(checked);
              }}
              disabled={integrationReadOnly}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledWebhooksSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              Webhook URL
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Zapier/Make webhook URL or your endpoint.
            </Typography>
            <TextField
              size="small"
              label="Webhook URL"
              placeholder="https://hooks.zapier.com/..."
              value={webhooksSettings.webhookUrl}
              onChange={(e) =>
                handleWebhooksSettingsChange("webhookUrl", e.target.value)
              }
              disabled={disabledWebhooksSection}
              fullWidth
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, opacity: disabledWebhooksSection ? 0.5 : 1 }}>
          <CardContent>
            <Typography fontWeight={700} mb={1}>
              API Key (optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Sent as X-API-Key header for webhook auth.
            </Typography>
            <TextField
              size="small"
              type="password"
              label="API Key"
              placeholder="Optional"
              value={webhooksSettings.apiKey}
              onChange={(e) =>
                handleWebhooksSettingsChange("apiKey", e.target.value)
              }
              disabled={disabledWebhooksSection}
              fullWidth
            />
          </CardContent>
        </Card>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveWebhooks}
              sx={{ textTransform: "none" }}
              disabled={integrationReadOnly}
            >
              Save Settings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      </Box>
    </Box>
</Box>
  );
}
